import React from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BrandLogo from "../shared/BrandLogo";
import FloatingTools from "../shared/FloatingTools";
import AccountMenu from "../shared/AccountMenu";
import { cn } from "../../lib/utils";
import { ICON_STROKE } from "../ui/icon";
import { navigateAppBack } from "../../utils/appHub";

const EmergencyLayout = ({
  children,
  title,
  subtitle,
  showBack = false,
  backTo,
  onBack,
  headerVariant = "light",
  headerRight,
  subHeader,
  footer,
  showLanguage = true,
  showLogo = false,
  maxWidth = "max-w-lg",
  className,
  contentClassName,
}) => {
  const navigate = useNavigate();
  const hasFooter = Boolean(footer);

  const handleBack = () => {
    if (onBack) onBack();
    else if (backTo) navigate(backTo);
    else navigateAppBack(navigate);
  };

  return (
    <div className={cn("min-h-screen bg-background flex flex-col", hasFooter && "pb-footer", className)}>
      <header
        className={cn(
          "sticky top-0 z-40 border-b pt-[env(safe-area-inset-top)]",
          headerVariant === "emergency"
            ? "bg-destructive border-destructive text-destructive-foreground"
            : "bg-card border-border"
        )}
      >
        <div className={cn("mx-auto px-4 py-3 flex items-center gap-3 min-h-14", maxWidth)}>
          {showBack ? (
            <button
              type="button"
              onClick={handleBack}
              className={cn(
                "min-h-12 min-w-12 flex items-center justify-center rounded-md shrink-0",
                headerVariant === "emergency" ? "hover:bg-white/10" : "hover:bg-muted"
              )}
              aria-label="Go back"
            >
              <ArrowLeft size={22} strokeWidth={ICON_STROKE} />
            </button>
          ) : showLogo ? (
            <BrandLogo logoClassName="!h-10 !max-h-10 sm:!h-14 sm:!max-h-14" />
          ) : null}

          <div className="flex-1 min-w-0">
            {title && (
              <h1 className={cn(
                "text-body font-bold leading-tight truncate",
                headerVariant === "emergency" ? "text-destructive-foreground" : "text-foreground"
              )}>
                {title}
              </h1>
            )}
            {subtitle && (
              <p className={cn(
                "text-caption truncate",
                headerVariant === "emergency" ? "text-destructive-foreground/85" : "text-muted-foreground"
              )}>
                {subtitle}
              </p>
            )}
          </div>
          {headerRight === undefined && headerVariant !== "emergency" ? <AccountMenu /> : headerRight}
        </div>
        {subHeader && <div className={cn("mx-auto px-4 pb-2", maxWidth)}>{subHeader}</div>}
      </header>

      <main className={cn("flex-1 mx-auto w-full px-4 py-6", maxWidth, contentClassName)}>{children}</main>

      {footer && (
        <div className="fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)]">{footer}</div>
      )}

      {showLanguage && (
        <FloatingTools
          className={cn(
            "fixed right-4 z-[60]",
            hasFooter ? "bottom-[calc(var(--footer-height)+0.5rem)]" : "bottom-4 pb-[env(safe-area-inset-bottom)]"
          )}
        />
      )}
    </div>
  );
};

export default EmergencyLayout;
